import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import useModalOutsideClick from '../../hooks/useModalOutsideClick';
import useSortedData from '../../hooks/useSortedData';
import './ViewCurrentChipCounts.css';
import { formatNumber } from '../../../../utils/helpers';

const ViewCurrentChipCounts = ({
  show,
  onClose,
  players,
  currentUsername,
  userPlayerNames
}) => {
  // Add the useModalOutsideClick hook to close when clicking outside
  useModalOutsideClick(show, onClose, "view-chip-counts-overlay");

  // Filter players with chip counts to be used in sorting
  const filteredPlayers = useMemo(() => {
    if (!players) return [];
    return players.filter(player => player.sportSpecificData?.currentChipCount > 0);
  }, [players]);

  // Use the useSortedData hook for sorting
  const { 
    sortedData: sortedPlayers, 
    requestSort, 
    sortConfig 
  } = useSortedData(
    filteredPlayers,
    'sportSpecificData.currentChipCount' // Initial sort key
  );

  // Helper to display sort indicators in the table header
  const getSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) return '';
    return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  if (!show) return null;

  return (
    <div className="view-chip-counts-overlay">
      <div className="view-chip-counts-dialog">
        <div className="view-chip-counts-header">
          <h3>Current Chip Counts</h3>
          <button className="view-chip-counts-close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="view-chip-counts-content">
          {sortedPlayers.length > 0 ? (
            <table className="chip-counts-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('name')}>PLAYER{getSortIndicator('name')}</th>
                  <th onClick={() => requestSort('sportSpecificData.currentChipCount')}>CHIPS{getSortIndicator('sportSpecificData.currentChipCount')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map((player) => {
                  const isCurrentUser = player.name.toLowerCase() === currentUsername?.toLowerCase();
                  const isUserPlayer = userPlayerNames.includes(player.name.toLowerCase());
                  return (
                    <tr 
                      key={player.name}
                      className={isCurrentUser ? 'current-user-row' : ''}
                    >
                      <td className="player-name">
                        {isUserPlayer && (
                          <span title="This player is on your team">⭐ </span>
                        )}
                        {player.name}
                      </td>
                      <td className="chip-count">
                        {formatNumber(player.sportSpecificData.currentChipCount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="no-chip-counts-message">No active chip counts available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

ViewCurrentChipCounts.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  players: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      sportSpecificData: PropTypes.shape({
        currentChipCount: PropTypes.number
      })
    })
  ).isRequired,
  currentUsername: PropTypes.string,
  userPlayerNames: PropTypes.arrayOf(PropTypes.string)
};

ViewCurrentChipCounts.defaultProps = {
  userPlayerNames: [],
};

export default ViewCurrentChipCounts; 